// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/transporte/types/index.ts
// ════════════════════════════════════════════════════════════════════════════

// ── Status ──────────────────────────────────────────────────────────────────

export type StatusCTe = 'autorizado' | 'cancelado' | 'rejeitado' | 'digitacao';
export type StatusMDFe = 'autorizado' | 'encerrado' | 'cancelado' | 'rejeitado' | 'digitacao';
export type StatusAverbacao = 'averbado' | 'pendente' | 'erro';

export function statusCTeDeCStat(cStat: string | null, isCancelado?: boolean): StatusCTe {
  if (isCancelado) return 'cancelado';
  if (!cStat || cStat === '0') return 'digitacao';
  if (cStat === '100') return 'autorizado';
  if (cStat === '101') return 'cancelado';
  return 'rejeitado';
}

export function statusMDFeDeCStat(cStat: string | null, isCancelado?: boolean, isEncerrado?: boolean): StatusMDFe {
  if (isCancelado) return 'cancelado';
  if (isEncerrado) return 'encerrado';
  if (!cStat || cStat === '0') return 'digitacao';
  if (cStat === '100') return 'autorizado';
  return 'digitacao';
}

/**
 * Situação do manifesto pela linha da listagem. `situacao` é o TEXTO devolvido pela SEFAZ
 * (não o código), então autorizado é ter protocolo; com número mas sem protocolo, a
 * transmissão não foi aceita e o manifesto pode ser transmitido de novo com o mesmo número.
 */
export function statusDoMDFe(d: Pick<MDFeResumoDto, 'numero' | 'protocolo' | 'isCancelado' | 'isEncerrado'>): StatusMDFe {
  if (d.isCancelado) return 'cancelado';
  if (d.isEncerrado) return 'encerrado';
  if (d.protocolo) return 'autorizado';
  return d.numero > 0 ? 'rejeitado' : 'digitacao';
}

// ── CT-e ─────────────────────────────────────────────────────────────────────

export interface CTeResumoDto {
  id: number;
  numero: number;
  serie: string | null;
  chave: string | null;
  situacao: string | null;
  protocolo: string | null;
  dtEmissao: string;
  isAmbienteProducao: boolean;
  isCancelado: boolean;
  remetenteNome: string | null;
  destinatarioNome: string | null;
  valorPrestacao: number;
}

export interface CTeDetalheDto extends CTeResumoDto {
  remetenteId: number | null;
  destinatarioId: number | null;
  natureza: string | null;
  cfop: string | null;
  tomador: number;
  modal: number;
  cargaDescricao: string | null;
  cargaValor: number;
  cargaPeso: number;
  valorFrete: number;
  eventos: CTeEventoDto[];
}

export interface CTeEventoDto {
  id: number;
  tipoEvento: number | null;
  situacao: string | null;
  protocolo: string | null;
  dtEvento: string;
}

export interface PaginadoCTeResponse {
  itens: CTeResumoDto[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
  /** Situações no período/busca inteiro (sem o filtro de situação). */
  contagem?: { autorizados: number; digitacao: number; cancelados: number; rejeitados: number };
}

/** Filtros das listas de transporte (CT-e, CT-e OS, MDF-e) — todos aplicados no servidor. */
export interface ListaTransporteFiltros {
  busca?: string;
  /** Status da tela ('todos' = sem filtro). */
  situacao?: string;
  /** Emissão, yyyy-MM-dd (dia local). */
  dtInicio?: string;
  dtFim?: string;
  ordenarPor?: 'emissao' | 'numero';
  decrescente?: boolean;
}

/** Parâmetros da API a partir dos filtros (vazios não vão). */
export const paramsDaLista = (f: ListaTransporteFiltros) => ({
  busca: f.busca?.trim() || undefined,
  situacao: f.situacao && f.situacao !== 'todos' ? f.situacao : undefined,
  dtInicio: f.dtInicio || undefined,
  dtFim: f.dtFim || undefined,
  ordenarPor: f.ordenarPor,
  decrescente: f.decrescente,
});

export interface EmitirCTeRequest {
  serie: string;
  natureza: string;
  cfop: string;
  modal: number;
  tomador: number;
  dtEmissao: string;
  remetenteId?: number;
  destinatarioId?: number;
  cargaTipoPredominante: string;
  cargaDescricao: string;
  cargaValor: number;
  cargaPesoKg: number;
  nfesVinculadas: string[];
  cClassTrib: string;
  aliqCbs: number;
  aliqIbs: number;
  valorFrete: number;
  veiculoId?: number;
  motoristaNome?: string;
}

export interface EmitirCTeOsRequest {
  serie: string;
  natureza: string;
  cfop: string;
  modal: number;
  tomador: number;
  dtEmissao: string;
  contratanteId?: number;
  servicoDescricao: string;
  servicoValor: number;
  cClassTrib: string;
  aliqCbs: number;
  aliqIbs: number;
  valorFrete: number;
  veiculoId?: number;
  motoristaNome?: string;
}

export interface CancelarCTeRequest {
  justificativa: string;
}

export interface CartaCorrecaoCTeRequest {
  textoCorrecao: string;
  campo: string;
}

export interface TransmitirCTeResponse {
  sucesso: boolean;
  cStat: string;
  xMotivo: string;
  protocolo: string | null;
  chave: string | null;
}

// ── MDF-e ─────────────────────────────────────────────────────────────────────

export interface MDFeResumoDto {
  id: number;
  numero: number;
  serie: string | null;
  chave: string | null;
  situacao: string | null;
  protocolo: string | null;
  dtEmissao: string;
  ufInicio: string | null;
  ufFim: string | null;
  isEncerrado: boolean;
  isCancelado: boolean;
  isAmbienteProducao: boolean;
  veiculoPlaca: string | null;
}

export interface MDFeEventoDto {
  id: number;
  tipoEvento: number | null;
  situacao: string | null;
  protocolo: string | null;
  dtEvento: string;
}

export interface PaginadoMDFeResponse {
  itens: MDFeResumoDto[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
  /** Situações no período/busca inteiro (sem o filtro de situação). */
  contagem?: { autorizados: number; encerrados: number; cancelados: number; naoAutorizados: number };
}

export interface DocumentoVinculadoMDFe {
  chave: string;
  tipo: 'CTe' | 'NFe';
}

export interface EmitirMDFeRequest {
  serie: string;
  dtEmissao: string;
  veiculoId?: number;
  reboqueId?: number;
  condutoresIds: number[];
  documentosVinculados: DocumentoVinculadoMDFe[];
  ufInicio: string;
  ufFim: string;
  ufsPercurso: string[];
  seguroResponsavel: number;
  seguradora: string;
  apolice: string;
  /** false = emitir este manifesto em homologação (teste), mesmo com o emitente em produção. */
  isAmbienteProducao?: boolean;
}

export interface EncerrarMDFeRequest {
  dtEncerramento: string;
  ufEncerramento: string;
  municipioEncerramento: string;
}

export interface CancelarMDFeRequest {
  justificativa: string;
}

export interface TransmitirMDFeResponse {
  sucesso: boolean;
  cStat: string;
  xMotivo: string;
  protocolo: string | null;
  chave: string | null;
  /** Id do manifesto gravado: é com ele que se transmite de novo, mantendo o número. */
  id?: number | null;
}

// ── Averbação ─────────────────────────────────────────────────────────────────

export interface MovAverbacaoDto {
  id: number;
  numeroCte: number;
  serieCte: string | null;
  chaveCte: string | null;
  chaveMdfe: string | null;
  seguradoraId: number;
  descricaoSeguradora: string | null;
  dtAverbacao: string;
  protocolo: string | null;
  valorAverbacao: number;
  valorCarga: number;
  isCancelado: boolean;
  statusAverbacao: string;
  tentativasEnvio: number;
  dtUltimaTentativa: string | null;
}

export interface AverbacaoResultadoDto {
  sucesso: boolean;
  protocolo: string | null;
  statusAverbacao: string | null;
  mensagemErro: string | null;
  tentativasRealizadas: number;
}

export interface AverbarRequest {
  chaveAcesso: string;
  tipoDocumento: 'CTe' | 'MDFe';
  xmlConteudo: string;
  valorCarga: number;
  numeroCte: number;
  serieCte?: string;
  empresaId: number;
}
